import { httpClient } from '@texas/api/httpClient/httpClient';
import { Composition } from '../compositionApi';
import { SimpleFileLink } from '../filesApi';
import { AutoUpdateRequest, AutoUpdateResponse } from '../autoUpdateTypes';

export const textMaxLength = 20;

export interface PaData {
  paNumber: string;
  supplierItemCode: string;
  fileIdentifier: string | null;
  fileId: number | null;
}

export interface GarpCompareData {
  versionName: string;
  matrixNodeXValue: number | null;
  matrixNodeYValue: number | null;
  articleNumber: string;
  matrixNodeXName: string;
  matrixNodeYName: string;
  branchId: number;
  branchName: string;
  variantId: number;
  variantName: string;
  mainVariantId: number | null;
  mainVariantName: string | null;
  articleId: number;
  folderId: number;
  compositions: Composition[];
  productApproval?: PaData;
}

export interface GarpCell {
  readonly photo: SimpleFileLink | null;
  readonly supplierColorCode: string | null;
  readonly supplierSize: string | null;
  readonly techniqueId: number | null;
  readonly materialId: number | null;
  readonly weight: number | null;
  readonly blockedFromSync: boolean;
  readonly variantId: number;
  readonly branchId: number;
}

export interface GarpBranch {
  readonly variantId: number;
  readonly variantName: string;
  readonly branchId: number;
  readonly branchIdentifier: string;
  readonly branchName: string;
  photo: SimpleFileLink | null;
  techniqueId: number | null;
  materialId: number | null;
  categoryCodeId: number | null;
  countryOfOriginId: number | null;
  customsCodeId: number | null;
  weight: number | null;
  texts: GarpText[];
}

export interface AvailableVersions {
  versions: { id: number; name: string; paId?: number }[];
}

export interface AutoUpdateGarpCellRequest {
  supplierColorCode: string | null;
  supplierSize: string | null;
  materialId: number | null;
  techniqueId: number | null;
  weight: number | null;
}

interface GarpText {
  index: number;
  languageCode: string;
  text: string;
}

export interface AutoUpdateGarpRequest {
  categoryCodeId: number | null;
  customsCodeId: number | null;
  countryOfOriginId: number | null;
  techniqueId: number | null;
  materialId: number | null;
  weight: number | null;
  texts: GarpText[];
}

export type AutoUpdateGarpResponse = AutoUpdateGarpRequest;

export type AutoUpdateGarpCellResponse = AutoUpdateGarpCellRequest;

export const garpApi = {
  importMetadata: (branchId: number) =>
    httpClient.post(`garp/branches/${branchId}/importmetadata`),
  importMatrixNodes: () => httpClient.post(`garp/importmatrixnodes`),
  getGarpAvailableVersions: (articleId: number, branchId: number) =>
    httpClient.get<AvailableVersions>(
      `garp/compare/article/${articleId}/branch/${branchId}/versions`,
    ),
  garpFriendlyData: (compositionGroupId: number) =>
    httpClient.get<GarpCompareData>(
      `garp/compare/composition-group/${compositionGroupId}`,
    ),
  garpFriendlyPaData: (compositionGroupId: number, productApprovalId: number) =>
    httpClient.get<GarpCompareData>(
      `garp/compare/composition-group/${compositionGroupId}`,
      { params: { productApprovalId } },
    ),
  garpCellPaCompareData: (
    variantId: number,
    branchId: number,
    cellX: number,
    cellY: number,
  ) =>
    httpClient.get<GarpCell>(
      `garp/compare/cell/${variantId}/${branchId}/${cellX}/${cellY}`,
    ),
  garpBranchPaCompareData: (variantId: number, branchId: number) =>
    httpClient.get<GarpBranch>(`garp/compare/${variantId}/${branchId}`),
  autoUpdateCell: (
    data: AutoUpdateRequest<AutoUpdateGarpCellRequest>,
    variantId: number,
    branchId: number,
    cellX: number,
    cellY: number,
  ) =>
    httpClient.put<AutoUpdateResponse<AutoUpdateGarpCellResponse>>(
      `garp/auto-update/${variantId}/${branchId}/${cellX}/${cellY}`,
      { data },
    ),
  autoUpdateGarp: (
    data: AutoUpdateRequest<AutoUpdateGarpRequest>,
    variantId: number,
    branchId: number,
  ) =>
    httpClient.put<AutoUpdateResponse<AutoUpdateGarpResponse>>(
      `garp/auto-update/${variantId}/${branchId}`,
      { data },
    ),
};
